import { useMemo } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTableType
} from 'modules/finance/components/FinancialWorkspace/types/table'

import { useDataCollectionSource } from './hooks/useDataCollectionSource'
import { useDataCollectionColumns } from './hooks/useDataCollectionColumns'
import { useHeaders } from './hooks/useHeaders'

interface DataCollectionTableProps {
  type?: InsightTableType
  groupBy: InsightTableGroupBy
  search?: string
}

/**
 * OneDataCollection-based table for Financial Workspace
 *
 * Renders financial data using OneDataCollection with support for:
 * - Dynamic columns based on date range and period selection
 * - Nested/collapsible rows for grouped data
 * - Comparison mode with growth indicators
 * - Infinite scroll pagination
 */
export const DataCollectionTable = ({ type, groupBy, search }: DataCollectionTableProps) => {
  const { hasComparisonDate, insightTableType } = useFinancialWorkspace()

  const effectiveType = insightTableType ?? type

  const source = useDataCollectionSource({
    type: effectiveType,
    groupBy,
    search
  })

  const headerData = useHeaders()

  // Use chartLabel (e.g., "Jan 2026") to match backend columnLabel format
  const headers = useMemo(() => {
    return headerData.map((h) => h.chartLabel || h.label)
  }, [headerData])

  const columns = useDataCollectionColumns({
    headers,
    groupBy,
    showComparison: hasComparisonDate
  })

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          frozenColumns: 1 as const,
          columns
        }
      }
    ],
    [columns]
  )

  return <OneDataCollection source={source} visualizations={visualizations} />
}
